import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuth } from '../hooks/useAuth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { uploadToImgbb } from '../utils/imgbb';

const PLAN_LABELS = { basic: 'Basic', pro: 'Pro', trial: 'トライアル' };

const AccountPage = () => {
  const { user, userProfile, isOwner, isLicenseExpired, loading: contextLoading } = useAuthContext();
  const { updateUserProfile, changePassword, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.state?.from ?? '/dashboard';

  const [profile, setProfile] = useState({
    vrchat_name: '', vrchat_profile: '', x_url: '', discord: '', image_url: '',
  });
  const fileInputRef = useRef(null);
  const [imgUploadStatus, setImgUploadStatus] = useState('idle'); // idle | uploading | done | error
  const [imgUploadErr,    setImgUploadErr]    = useState('');
  const [saveStatus, setSaveStatus]   = useState('idle'); // idle | saving | saved | error
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError]     = useState('');
  const [pwSection, setPwSection]     = useState(false);
  const [currentPw, setCurrentPw]     = useState('');
  const [newPw, setNewPw]             = useState('');
  const [confirmPw, setConfirmPw]     = useState('');
  const [pwStatus, setPwStatus]       = useState('idle'); // idle | saving | saved | error
  const [pwErrMsg, setPwErrMsg]       = useState('');

  useEffect(() => {
    if (!userProfile) return;
    setProfile({
      vrchat_name:    userProfile.vrchat_name    || '',
      vrchat_profile: userProfile.vrchat_profile || '',
      x_url:          userProfile.x_url          || '',
      discord:        userProfile.discord        || '',
      image_url:      userProfile.image_url      || '',
    });
  }, [userProfile]);

  if (contextLoading) return (
    <div style={centerStyle}>
      <p style={{ color: '#94a3b8' }}>読み込み中...</p>
    </div>
  );

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setImgUploadErr('画像ファイルを選択してください'); return; }
    if (file.size > 32 * 1024 * 1024) { setImgUploadErr('32MB以下の画像を選択してください'); return; }

    setImgUploadStatus('uploading');
    setImgUploadErr('');
    try {
      const url = await uploadToImgbb(file);
      setProfile(p => ({ ...p, image_url: url }));
      await updateUserProfile(user.uid, { image_url: url });
      setImgUploadStatus('done');
      setTimeout(() => setImgUploadStatus('idle'), 2000);
    } catch (err) {
      setImgUploadErr(err.message || 'アップロードに失敗しました');
      setImgUploadStatus('error');
    }
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!profile.vrchat_name.trim()) {
      setSaveStatus('error');
      return;
    }
    setSaveStatus('saving');
    const ok = await updateUserProfile(user.uid, {
      ...profile,
      display_name: profile.vrchat_name.trim(),
    });
    setSaveStatus(ok ? 'saved' : 'error');
    if (ok) setTimeout(() => setSaveStatus('idle'), 2000);
  };

  const handleChangePassword = async () => {
    setPwErrMsg('');
    if (!currentPw || !newPw || !confirmPw) { setPwErrMsg('すべての項目を入力してください'); return; }
    if (newPw.length < 6) { setPwErrMsg('新しいパスワードは6文字以上で設定してください'); return; }
    if (newPw !== confirmPw) { setPwErrMsg('新しいパスワードが一致しません'); return; }
    setPwStatus('saving');
    const result = await changePassword(currentPw, newPw);
    if (result?.ok) {
      setPwStatus('saved');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => { setPwStatus('idle'); setPwSection(false); }, 2000);
    } else {
      setPwStatus('error');
      setPwErrMsg(result?.message || 'パスワードの変更に失敗しました');
    }
  };

  const handleChangePlan = async (targetPlan) => {
    setPlanLoading(true);
    setPlanError('');
    try {
      const fns = getFunctions(undefined, 'asia-northeast1');
      const createSession = httpsCallable(fns, 'createCheckoutSession');
      const result = await createSession({ plan: targetPlan, returnUrl: window.location.href });
      window.location.replace(result.data.url);
    } catch (err) {
      console.error('createCheckoutSession failed:', err);
      setPlanError('エラーが発生しました。もう一度お試しください。');
      setPlanLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPlanLoading(true);
    setPlanError('');
    try {
      const fns = getFunctions(undefined, 'asia-northeast1');
      const createPortal = httpsCallable(fns, 'createPortalSession');
      const result = await createPortal({ returnUrl: window.location.href });
      window.location.replace(result.data.url);
    } catch (err) {
      console.error('createPortalSession failed:', err);
      setPlanError(
        err?.code === 'functions/not-found'
          ? 'ご契約中のサブスクリプションが見つかりませんでした。'
          : 'エラーが発生しました。もう一度お試しください。',
      );
      setPlanLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };


  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fb', padding: '32px 16px 64px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 520 }}>

        {/* ヘッダー */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <button
            onClick={() => navigate(backPath)}
            style={{ background: 'none', border: 'none', fontSize: 13, color: '#6c63ff', fontWeight: 600, cursor: 'pointer', padding: 0 }}
          >
            ← 戻る
          </button>
        </div>

        <h1 style={{ margin: '0 0 24px', fontSize: 22, fontWeight: 800, color: '#0f172a' }}>アカウント設定</h1>

        {/* ライセンス情報カード（ownerのみ） */}
        {isOwner && (() => {
          const lic = userProfile?.license;
          const plan = lic?.plan;
          const expiresAt = lic?.expires_at;
          const planLabel = PLAN_LABELS[plan?.toLowerCase()] ?? plan ?? '—';
          const expiryText = expiresAt
            ? new Date(expiresAt).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })
            : '設定なし';

          return (
            <div style={{ ...card, marginBottom: 16 }}>
              <h2 style={sectionTitle}>ライセンス情報</h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={fieldLabel}>現在のプラン</p>
                  <span style={{
                    fontSize: 13, fontWeight: 700, padding: '3px 12px', borderRadius: 20,
                    background: plan === 'pro' ? '#f0fdf4' : plan === 'trial' ? '#fef9c3' : '#f1f5f9',
                    color: plan === 'pro' ? '#16a34a' : plan === 'trial' ? '#854d0e' : '#475569',
                    border: `1px solid ${plan === 'pro' ? '#bbf7d0' : plan === 'trial' ? '#fde68a' : '#e2e8f0'}`,
                  }}>
                    {planLabel}
                  </span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={fieldLabel}>有効期限</p>
                  <span style={{
                    fontSize: 13, fontWeight: 600,
                    color: isLicenseExpired ? '#dc2626' : '#334155',
                  }}>
                    {isLicenseExpired ? `⚠️ 期限切れ（${expiryText}）` : expiryText}
                  </span>
                </div>
              </div>

              {/* プラン変更ボタン */}
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {plan !== 'pro' && (
                  <button
                    onClick={() => handleChangePlan('pro')}
                    disabled={planLoading}
                    style={{ ...primaryBtn, fontSize: 13, opacity: planLoading ? 0.7 : 1 }}
                  >
                    {planLoading ? '処理中...' : 'Pro にアップグレード（¥2,000/月）'}
                  </button>
                )}
                {plan === 'trial' && (
                  <button
                    onClick={() => handleChangePlan('basic')}
                    disabled={planLoading}
                    style={{ ...outlineBtn, width: '100%', fontSize: 13, opacity: planLoading ? 0.7 : 1 }}
                  >
                    {planLoading ? '処理中...' : 'Basic に申し込む（¥1,000/月）'}
                  </button>
                )}
                {(plan === 'basic' || plan === 'pro') && (
                  <button
                    onClick={handleManageSubscription}
                    disabled={planLoading}
                    style={{ background: 'none', border: 'none', fontSize: 12, color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline', padding: '4px 0', opacity: planLoading ? 0.7 : 1 }}
                  >
                    解約・支払い管理
                  </button>
                )}
                {planError && (
                  <p style={{ margin: 0, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{planError}</p>
                )}
              </div>

              {isLicenseExpired && (
                <p style={{ margin: '14px 0 0', fontSize: 12, color: '#dc2626', fontWeight: 600, lineHeight: 1.6 }}>
                  ライセンスの有効期限が切れています。管理者にお問い合わせください。
                </p>
              )}
            </div>
          );
        })()}

        {/* プロフィールカード */}
        <div style={card}>
          <h2 style={sectionTitle}>プロフィール情報</h2>

          {/* プロフィール画像 */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                position: 'relative', width: 88, height: 88, borderRadius: '50%',
                background: profile.image_url ? 'transparent' : 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
                cursor: 'pointer', overflow: 'hidden',
                boxShadow: '0 4px 16px rgba(108,99,255,0.25)',
                border: '3px solid #fff',
                outline: '2px solid #e2e8f0',
              }}
            >
              {profile.image_url ? (
                <img src={profile.image_url} alt="プロフィール" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, fontWeight: 800, color: '#fff' }}>
                  {(userProfile?.vrchat_name || userProfile?.display_name || '?').charAt(0).toUpperCase()}
                </span>
              )}
              {/* ホバー時のオーバーレイ */}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: imgUploadStatus === 'uploading' ? 1 : 0,
                transition: 'opacity 0.15s',
                fontSize: 20,
              }}
                onMouseEnter={e => { if (imgUploadStatus !== 'uploading') e.currentTarget.style.opacity = 1; }}
                onMouseLeave={e => { if (imgUploadStatus !== 'uploading') e.currentTarget.style.opacity = 0; }}
              >
                {imgUploadStatus === 'uploading' ? '⏳' : '📷'}
              </div>
            </div>

            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
              {imgUploadStatus === 'uploading' ? 'アップロード中...' :
               imgUploadStatus === 'done'      ? '✓ 画像を更新しました' :
               '画像をクリックして変更'}
            </p>
            {imgUploadErr && (
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#dc2626', fontWeight: 600 }}>{imgUploadErr}</p>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="VRChat 表示名" required>
              <input value={profile.vrchat_name} onChange={e => setProfile(p => ({ ...p, vrchat_name: e.target.value }))}
                placeholder="あなたのVRChatでの表示名" style={input} />
            </Field>

            <Field label="VRChat プロフィールURL" required
              hint="vrchat.com/home/user/usr_... の形式">
              <input value={profile.vrchat_profile} onChange={e => setProfile(p => ({ ...p, vrchat_profile: e.target.value }))}
                placeholder="https://vrchat.com/home/user/usr_..." style={input} />
            </Field>

            <Field label="X (Twitter) URL" hint="任意">
              <input value={profile.x_url} onChange={e => setProfile(p => ({ ...p, x_url: e.target.value }))}
                placeholder="https://x.com/yourname" style={input} />
            </Field>

            <Field label="Discord ID" hint="任意">
              <input value={profile.discord} onChange={e => setProfile(p => ({ ...p, discord: e.target.value }))}
                placeholder="username または username#0000" style={input} />
            </Field>
          </div>

          {saveStatus === 'error' && (
            <p style={{ margin: '12px 0 0', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
              {!profile.vrchat_name.trim() ? 'VRChat表示名は必須です' : '保存に失敗しました'}
            </p>
          )}
          {saveStatus === 'saved' && (
            <p style={{ margin: '12px 0 0', fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ 保存しました</p>
          )}

          <button onClick={handleSave} disabled={loading || saveStatus === 'saving'}
            style={{ ...primaryBtn, marginTop: 20, opacity: (loading || saveStatus === 'saving') ? 0.7 : 1 }}>
            {saveStatus === 'saving' ? '保存中...' : '変更を保存'}
          </button>
        </div>

        {/* アカウント情報カード */}
        <div style={{ ...card, marginTop: 16 }}>
          <h2 style={sectionTitle}>アカウント情報</h2>

          <div>
            <p style={fieldLabel}>メールアドレス</p>
            <p style={{ margin: 0, fontSize: 14, color: '#334155', fontWeight: 500 }}>{user.email}</p>
          </div>

          {/* パスワード変更 */}
          <button onClick={() => setPwSection(v => !v)}
            style={{ ...outlineBtn, marginTop: 16 }}>
            {pwSection ? 'キャンセル' : 'パスワードを変更する'}
          </button>

          {pwSection && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field label="現在のパスワード">
                <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
                  placeholder="現在のパスワード" style={input} />
              </Field>
              <Field label="新しいパスワード" hint="6文字以上">
                <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                  placeholder="新しいパスワード（6文字以上）" style={input} />
              </Field>
              <Field label="新しいパスワード（確認）">
                <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                  placeholder="もう一度入力" style={input} />
              </Field>

              {pwErrMsg && <p style={{ margin: 0, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{pwErrMsg}</p>}
              {pwStatus === 'saved' && <p style={{ margin: 0, fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ パスワードを変更しました</p>}

              <button onClick={handleChangePassword} disabled={pwStatus === 'saving'}
                style={{ ...primaryBtn, opacity: pwStatus === 'saving' ? 0.7 : 1 }}>
                {pwStatus === 'saving' ? '変更中...' : 'パスワードを変更する'}
              </button>
            </div>
          )}
        </div>

        {/* ログアウト */}
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <button onClick={handleLogout}
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
            ログアウト
          </button>
        </div>
      </div>
    </div>
  );
};

// ── サブコンポーネント ─────────────────────────────────────────────────────
const Field = ({ label, required, hint, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 4 }}>
      {label}
      {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
      {hint && <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{hint}</span>}
    </label>
    {children}
  </div>
);

// ── スタイル定数 ─────────────────────────────────────────────────────────
const card = {
  background: '#fff', borderRadius: 16, padding: '24px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e8edf5',
};

const sectionTitle = {
  margin: '0 0 18px', fontSize: 15, fontWeight: 800, color: '#0f172a',
};

const fieldLabel = {
  margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: '#64748b',
};

const input = {
  width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0',
  fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fff',
  transition: 'border-color 0.15s',
};

const primaryBtn = {
  width: '100%', padding: '11px', borderRadius: 10, border: 'none',
  background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
  color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};

const outlineBtn = {
  padding: '9px 18px', borderRadius: 8, border: '1.5px solid #6c63ff',
  background: '#fff', color: '#6c63ff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
};

const centerStyle = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#f4f6fb',
};

export default AccountPage;
